import type {
  TemplateOperationContext,
  TemplateOperationErrorCode,
  TemplateOperationCompleteEvent,
  TemplateOperationResult,
} from './types';

export type TemplateOperationAuditOutcome =
  | 'success'
  | 'validation_failure'
  | 'cancelled'
  | 'authorization_failure'
  | 'unexpected_failure';

export type { TemplateOperationCompleteEvent };

function mapOutcome(result: TemplateOperationResult): {
  outcome: TemplateOperationAuditOutcome;
  errorCode?: string;
} {
  if (result.ok) {
    if (result.data.settlementStatus === 'cancelled') {
      return { outcome: 'cancelled' };
    }
    return { outcome: 'success' };
  }

  const code = result.error.code;
  if (code === 'UNAUTHORIZED') {
    return { outcome: 'authorization_failure', errorCode: code };
  }
  if (code === 'INVALID_INPUT' || code === 'STALE_REVISION' || code === 'UNSUPPORTED') {
    return { outcome: 'validation_failure', errorCode: code };
  }
  return { outcome: 'unexpected_failure', errorCode: code };
}

export function dispatchTemplateOperationAudit(
  context: TemplateOperationContext,
  operation: string,
  inputArgs: Record<string, unknown>,
  startedAt: number,
  result: TemplateOperationResult,
): void {
  const reporter = context.onOperationComplete;
  if (!reporter) {
    return;
  }

  const completedAt = Date.now();
  try {
    reporter({
      invocationId: result.invocationId,
      operation,
      source: context.source,
      toolName: context.toolName,
      templateId: context.templateId,
      startedAt,
      completedAt,
      durationMs: completedAt - startedAt,
      result,
      inputArgs,
    });
  } catch {
    // Audit delivery must not affect the working copy.
  }
}

export function buildAuditOutcomeFields(result: TemplateOperationResult): {
  outcome: TemplateOperationAuditOutcome;
  errorCode?: TemplateOperationErrorCode;
  workingRevision?: number;
  outputSummary?: Record<string, unknown>;
} {
  const { outcome } = mapOutcome(result);
  if (result.ok) {
    return {
      outcome,
      workingRevision: result.data.workingRevision,
      outputSummary: {
        operation: result.data.operation,
        workingRevision: result.data.workingRevision,
        ...(result.data.applied != null ? { applied: result.data.applied } : {}),
        ...(result.data.saved != null ? { saved: result.data.saved } : {}),
        ...(result.data.settlementStatus != null
          ? { settlementStatus: result.data.settlementStatus }
          : {}),
      },
    };
  }
  return {
    outcome,
    errorCode: result.error.code,
    outputSummary: {
      code: result.error.code,
      message: result.error.message,
    },
  };
}
